import React from 'react';
import { 
  Typography, 
  Box,
  Chip,
  LinearProgress,
  Skeleton,
  Tooltip
} from '@mui/material';
import InsightCard from './shared/InsightCard';
import PersonIcon from '@mui/icons-material/Person';
import RepeatIcon from '@mui/icons-material/Repeat';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import SleepingIcon from '@mui/icons-material/Bedtime';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

type EngagementLevel = 'new_customer' | 'active' | 'core' | 'dormant';

interface EngagementInfo {
  level: EngagementLevel;
  label: string;
  icon: React.ReactNode;
  color: {
    bg: string;
    text: string;
    theme: string;
  };
}

interface CustomerStatusCardProps {
  data: {
    engagementLevel: EngagementLevel;
    repeatRate: number;
    lastVisitDate: Date | null;
    totalVisits: number;
  } | null;
  loading?: boolean;
  treatmentRecords?: Array<{
    id: string;
    treatmentDate: string;
    startTime?: string;
  }>;
}

const CustomerStatusCard: React.FC<CustomerStatusCardProps> = ({ data, loading = false }) => {
  const getEngagementInfo = (level: EngagementLevel): EngagementInfo => {
    switch (level) {
      case 'new_customer':
        return {
          level,
          label: '新規顧客',
          icon: <PersonIcon sx={{ color: 'primary.main' }} />,
          color: {
            bg: '#e3f2fd',
            text: '#0277bd',
            theme: 'primary'
          }
        };
      case 'active':
        return {
          level,
          label: 'アクティブ',
          icon: <TrendingUpIcon sx={{ color: 'success.main' }} />,
          color: {
            bg: '#e8f5e9',
            text: '#2e7d32',
            theme: 'success'
          }
        };
      case 'core':
        return {
          level,
          label: 'コア顧客',
          icon: <TrendingFlatIcon sx={{ color: 'warning.main' }} />,
          color: {
            bg: '#fff8e1',
            text: '#f57c00',
            theme: 'warning'
          }
        };
      case 'dormant':
        return {
          level,
          label: '休眠顧客',
          icon: <SleepingIcon sx={{ color: 'error.main' }} />,
          color: {
            bg: '#ffebee',
            text: '#c62828',
            theme: 'error'
          }
        };
    }
  };

  const calculateEngagementLevel = (totalVisits: number, lastVisitDate: Date | null): EngagementLevel => {
    if (!lastVisitDate) return 'new_customer';
    
    const daysSinceLastVisit = Math.floor((new Date().getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (totalVisits === 1 || daysSinceLastVisit <= 30) {
      return totalVisits === 1 ? 'new_customer' : 'active';
    }
    
    if (daysSinceLastVisit <= 60 && totalVisits >= 5) {
      return 'core';
    }
    
    if (daysSinceLastVisit > 60) {
      return 'dormant';
    }
    
    return 'active';
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '---';
    const d = new Date(date);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <InsightCard
        title="顧客ステータス"
        cardVariant="warning"
      >
        <Skeleton variant="rectangular" width={100} height={32} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="70%" height={24} sx={{ mt: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={40} sx={{ mt: 2 }} />
      </InsightCard>
    );
  }

  if (!data) {
    return (
      <InsightCard
        title="顧客ステータス"
        cardVariant="warning"
      >
        <Typography color="text.secondary">
          データがありません
        </Typography>
      </InsightCard>
    );
  }

  const engagementLevel = data.engagementLevel || calculateEngagementLevel(data.totalVisits, data.lastVisitDate);
  const engagementInfo = getEngagementInfo(engagementLevel);

  return (
    <InsightCard
      title="顧客ステータス"
      cardVariant="warning"
    >
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {engagementInfo.icon}
            <Typography variant="body2" color="text.secondary">
              エンゲージメントレベル
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={engagementInfo.label}
              sx={{
                bgcolor: engagementInfo.color.bg,
                color: engagementInfo.color.text,
                fontWeight: 'bold',
                fontSize: '1rem',
                px: 2,
                py: 0.5,
                minWidth: '120px',
                justifyContent: 'center'
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <RepeatIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            <Tooltip 
              title="初回来店から最終来店までの期間で、実際に来店があった月の割合です。継続的な来店頻度を示す指標となります。"
              arrow
              placement="top"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    borderBottom: '1px dotted rgba(0, 0, 0, 0.42)'
                  }}
                >
                  リピート率
                </Typography>
                <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              </Box>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
              {data.repeatRate}%
            </Typography>
            <Box sx={{ flex: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={data.repeatRate} 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'warning.100',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'warning.main',
                    borderRadius: 4
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <PersonIcon sx={{ color: 'warning.main', fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary">
                総来店回数
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ color: 'warning.main' }}>
              {data.totalVisits}回
            </Typography>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <EventAvailableIcon sx={{ color: 'warning.main', fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary">
                最終来店日
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: 'warning.main', fontWeight: 'medium' }}>
              {formatDate(data.lastVisitDate)}
            </Typography>
          </Box>
        </Box>
    </InsightCard>
  );
};

export default CustomerStatusCard;